/*
 * Pre-login configuration for the current MYS client (MYS-7187).
 *
 * The login page has to know whether this client logs in through an external
 * identity provider before there is any user to authenticate, so we cannot use
 * the store's selectedClient (which only exists after login). Instead we resolve
 * it from the hostname - customers reach MYS through their own domain aliased to
 * the MYS domain - via the unauthenticated GET /api/login-config.
 *
 * resolveLoginConfig() is awaited in App.beforeMount() whenever needsLoginConfig()
 * says the page load can show a logged out view, which is guaranteed to complete
 * before any route component mounts because <router-view> is gated on `initialized`.
 * The paths that reach the login without a page load (App.showLogin(), the redirect
 * in Login.vue) await it themselves; it resolves once per page load.
 */

// Staging (and local dev) serve every customer from one host, so the client cannot
// be derived from the hostname there. ?clientId= overrides it, and we remember the
// override because it is lost across the Keycloak redirect round trip.
// ATT: App.showLogin() clears localStorage - this key is in its keep list.
const LS_CLIENT_ID = 'mysLoginClientId'

/*
 * ...but only on a host that is shared by every customer. On a customer's own domain
 * the hostname already identifies the client, and honouring an override there would let
 * a crafted link (https://customer-domain/?clientId=OTHER) pin that browser to another
 * client's login config for good - the key deliberately survives the logout wipe, so
 * there would be no way back short of devtools. On such a host we ignore the parameter
 * and drop anything already stored, so a poisoned browser heals itself on the next load.
 */
function isSharedHost(hostname: string): boolean {
	// the generic urls are myservices.<domain> - a customer is <customer>.myservices.<domain>
	return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.split('.')[0] === 'myservices'
}

// mirrors LoginConfig in shared/sso.ts, which is what /api/login-config returns.
// declared locally on purpose: this is the only plain .ts file in dashboard/ and a
// cross package import here breaks the dashboard tsconfig (the other cross package
// imports all live in .vue files, which tsc never parses).
type LoginConfig = {
	clientId?: string
	ssoEnabled: boolean
	/** Keycloak identity provider alias, passed on as kc_idp_hint */
	idpHint?: string
	passwordLoginEnabled: boolean
}

const DEFAULT_LOGIN_CONFIG: LoginConfig = {
	ssoEnabled: false,
	passwordLoginEnabled: true,
}

let config: LoginConfig = DEFAULT_LOGIN_CONFIG

export function getLoginConfig(): LoginConfig {
	return config
}

/** the remembered ?clientId= override, if any - see LS_CLIENT_ID above */
export function getLoginClientIdOverride(): string | null {
	return localStorage.getItem(LS_CLIENT_ID)
}

/**
 * Stores or drops the ?clientId= override of this page load. Cheap and synchronous, so it
 * runs on every load - also for sessions that skip resolveLoginConfig() - otherwise a
 * tester who opens ?clientId= while still logged in would log out to the wrong client.
 */
export function rememberLoginClientIdOverride() {
	// an empty ?clientId= clears the override, so a tester can get back to hostname
	// resolution without reaching for devtools
	const override = new URLSearchParams(location.search).get('clientId')
	if (!isSharedHost(location.hostname) || override === '') localStorage.removeItem(LS_CLIENT_ID)
	else if (override) localStorage.setItem(LS_CLIENT_ID, override)
}

// the routes that render a logged out view and read the config synchronously on mount
const LOGIN_FLOW_PATHS = ['/', '/auth', '/auth/callback', '/forgotPassword']

/**
 * Whether a page load has to resolve the config before first render. A logged in session
 * on any other route never reads it, so it skips the round trip (and the content lookup
 * behind it). The login routes are needed even when logged in: the stored user outlives
 * the Keycloak session, and '/' with a stale user must still hand an SSO client to its
 * identity provider.
 */
export function needsLoginConfig(pathname: string, loggedIn: boolean): boolean {
	return !loggedIn || LOGIN_FLOW_PATHS.includes(pathname)
}

// at most one request per page load - the hostname and the override cannot change
// without a reload, so later callers (a login redirect, an in-app logout) reuse it
let pending: Promise<LoginConfig> | null = null

export function resolveLoginConfig(httpGet: (url: string, query?: any) => Promise<any>): Promise<LoginConfig> {
	if (!pending) pending = fetchLoginConfig(httpGet)
	return pending
}

async function fetchLoginConfig(httpGet: (url: string, query?: any) => Promise<any>): Promise<LoginConfig> {
	const query: Record<string, string> = { hostUrl: location.hostname }
	const clientId = getLoginClientIdOverride()
	if (clientId) query.clientId = clientId

	try {
		config = await httpGet('/login-config', query) || DEFAULT_LOGIN_CONFIG
	}
	catch (e) {
		// the endpoint already fails open, so this is a network/proxy problem -
		// fall back to the regular password login rather than blocking the page
		console.warn('login-config unavailable, falling back to password login', e)
		config = DEFAULT_LOGIN_CONFIG
	}

	return config
}
